import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { KeyRound, LayoutDashboard, LogOut, UserPen } from 'lucide-react';
import type { CandidateMe } from '../lib/apiClient';

interface AvatarMenuProps {
  candidate: CandidateMe;
  onSignOut: () => void;
  /** Optional avatar image URL — falls back to initials when absent. v1's CandidateMe shape
   *  doesn't carry one yet (Phase 7-dashboard adds profile-photo upload), so callers usually
   *  pass null. */
  photoUrl?: string | null;
  /** When the menu's open state matters to the parent (for mobile-menu coordination). */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Top-right circular avatar trigger + dropdown menu. Phase 7-avatar replaces the minimal
 * "Hi, {name} · Sign Out" chip from Phase 6.0.
 *
 * Menu items (in order):
 *   1. My Dashboard           → /dashboard (Phase 7-dashboard-b)
 *   2. Update Profile         → /profile (redirects to /dashboard/profile)
 *   3. Change Password        → /change-password
 *   4. Sign Out               → triggers AuthContext.signOut()
 */
export function AvatarMenu({ candidate, onSignOut, photoUrl, onOpenChange }: AvatarMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  function setOpenWithNotify(next: boolean) {
    setOpen(next);
    onOpenChange?.(next);
  }

  // Close on outside click. Listening on document instead of a backdrop element so the
  // surrounding nav and content remain interactive (clicking a Link on the page closes
  // the menu rather than fighting the overlay).
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node | null;
      if (target && containerRef.current && !containerRef.current.contains(target)) {
        setOpenWithNotify(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpenWithNotify(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const initials = computeInitials(candidate.full_name);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpenWithNotify(!open)}
        className="rounded-full border border-[#1caa3c] bg-[#14150f] p-0.5 transition-colors hover:bg-[#0b5c22] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1caa3c] focus-visible:ring-offset-2 focus-visible:ring-offset-[#f5f6f2]"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Open account menu"
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt=""
            className="h-8 w-8 rounded-full object-cover sm:h-9 sm:w-9"
            referrerPolicy="no-referrer"
          />
        ) : (
          <span className="flex h-8 w-8 select-none items-center justify-center rounded-full font-['JetBrains_Mono',monospace] text-xs font-semibold tracking-wide text-white sm:h-9 sm:w-9 sm:text-sm">
            {initials}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            aria-label="Account menu"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.14, ease: 'easeOut' }}
            // On small viewports the dropdown stretches near full-width centred under the
            // avatar; on md+ it anchors to the avatar's top-right corner.
            className="absolute right-0 top-full z-50 mt-2 w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-[2px] border border-black/15 border-t-2 border-t-[#1caa3c] bg-[#f5f6f2] font-['Inter',sans-serif] shadow-[0_18px_45px_rgba(20,21,15,0.16)]"
          >
            <div className="border-b border-black/10 px-4 py-4">
              <div className="flex items-center gap-3">
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt=""
                    className="h-10 w-10 rounded-full border border-[#1caa3c] object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[#1caa3c] bg-[#14150f] font-['JetBrains_Mono',monospace] text-sm font-semibold text-white">
                    {initials}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-['Poppins',sans-serif] text-sm font-semibold text-[#14150f]">
                    {candidate.full_name || 'Candidate'}
                  </p>
                  <p className="truncate text-xs text-[#6b6f68]">{candidate.email}</p>
                </div>
              </div>
            </div>

            <nav className="py-2">
              <MenuItem
                to="/dashboard"
                icon={<LayoutDashboard className="w-4 h-4" />}
                onClick={() => setOpenWithNotify(false)}
              >
                My Dashboard
              </MenuItem>
              <MenuItem
                to="/dashboard/profile"
                icon={<UserPen className="w-4 h-4" />}
                onClick={() => setOpenWithNotify(false)}
              >
                Update Profile
              </MenuItem>
              <MenuItem
                to="/change-password"
                icon={<KeyRound className="w-4 h-4" />}
                onClick={() => setOpenWithNotify(false)}
              >
                Change Password
              </MenuItem>
            </nav>

            <div className="border-t border-black/10 py-2">
              <button
                type="button"
                onClick={() => {
                  setOpenWithNotify(false);
                  onSignOut();
                }}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium text-[#14150f] transition-colors hover:bg-black/[0.05] hover:text-[#0b5c22] focus-visible:bg-black/[0.05] focus-visible:text-[#0b5c22] focus-visible:outline-none"
                role="menuitem"
              >
                <LogOut className="h-4 w-4 text-[#0b5c22]" />
                Sign Out
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MenuItem({
  to,
  icon,
  onClick,
  children,
}: {
  to: string;
  icon: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-[#14150f] transition-colors hover:bg-black/[0.05] hover:text-[#0b5c22] focus-visible:bg-black/[0.05] focus-visible:text-[#0b5c22] focus-visible:outline-none [&>svg]:text-[#0b5c22]"
      role="menuitem"
    >
      {icon}
      {children}
    </Link>
  );
}

/** First letter of first word + first letter of last word (uppercased). Falls back to "?". */
function computeInitials(fullName: string): string {
  const trimmed = (fullName ?? '').trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || '?';
}
